<template lang="pug">
div
    resource-table(
        v-if="shouldRenderTable"
        item-key="id"
        :can-show-admin-resources="showAdminResources"
        :can-manage-own-resources="isSaveEnabled"
        :headers="headers"
        :items="tagsData"
        :singular-resource="singularResource"
        :plural-resource="pluralResource"
        :place-holder-item="placeHolderItem"
        :should-save="isFormValid"
        :selected-label="selectedLabel"
        :is-loading="isLoading"
        :affected-count="affectedContacts"
        :show-success-message="showSuccess"
        :show-error-message="showError"
        additional-message="This is an additional space for a message. "
        @create="handleCreate"
        @edit="handleEdit"
        @cancel="handleCancel"
        @selected-item="setSelectedId"
        @clear-messages="clearMessages"
        @save:create="handleSaveCreate"
        @save:edit="handleSaveEdit"
    )
        template(slot="items" slot-scope="props")
            td
                editable-text-field(
                    v-model="props.item.content"
                    :is-editing="shouldEdit(props.item)"
                    :rules="[rules.required, rules.noEmpty, rules.maxLength, isUnique]"
                    @update:error="handleError"
                )
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import { CONTACTS } from '../config';

import LiondeskDialog from '@/Core/components/LiondeskDialog';
import ResourceTable from './ResourceTable';
import EditableTextField from './EditableTextField';
import EditableDropDown from './EditableDropDown';
import GetSubAccountPermissions from '../mixins/getSubAccountPermissions';
import GetAffectedContacts from '../mixins/getAffectedContacts';

const {
    mapActions: mapContactTagsActions,
    mapState: mapContactTagsState,
    mapMutations: mapContactTagsMutations,
} = createNamespacedHelpers('contactTags');

export default {
    name: 'ContactTags',
    mixins: [GetSubAccountPermissions, GetAffectedContacts],
    components: {
        LiondeskDialog,
        ResourceTable,
        EditableTextField,
        EditableDropDown,
    },
    data() {
        return {
            shouldRenderTable: false,
            resourcePermissionsType: CONTACTS.tags.resourceType,
            selectedId: null,
            isFormValid: true,
            singularResource: CONTACTS.tags.singularResource,
            pluralResource: CONTACTS.tags.pluralResources,
            headers: CONTACTS.tags.headers,
            placeHolderItem: CONTACTS.tags.placeHolderItem,
            rules: {
                required: v => !!v || 'Required',
                noEmpty: v => (v && v.trim().length > 0) || 'Tag cannot be empty',
                maxLength: v => (v && v.length <= 200) || 'Tag must be equal to or less than 200 characters',
            },
            isDelete: false,
        };
    },
    async mounted() {
        await this.getAllTags();
        this.shouldRenderTable = true;
        this.clearMessages();
    },
    watch: {
        async selectedId(value) {
            if (value) {
                await this.findTotalContactsByResource({ source_id: value });
            }
            this.isFormValid = true;
        },
    },
    computed: {
        ...mapContactTagsState(['data', 'hasError', 'errorMsg', 'hasSuccess', 'isLoading']),
        tagsData() {
            return JSON.parse(JSON.stringify(this.data));
        },
        selectedLabel() {
            const item = this.data.find(i => i.id === this.selectedId);
            return item && item.content;
        },
        showError: {
            get() {
                return this.hasError;
            },
            set(value) {
                this.clearMessages();
                this.clearAffectedContactsMessage();
            },
        },
        showSuccess: {
            get() {
                return this.hasSuccess;
            },
            set(value) {
                this.clearMessages();
                this.clearAffectedContactsMessage();
            },
        },
    },
    methods: {
        ...mapContactTagsActions([
            'getAllTags',
            'saveTag',
            'updateTag',
        ]),
        ...mapContactTagsMutations(['clearMessages']),
        handleEdit(itemId) {
            this.selectedId = itemId;
        },
        shouldEdit(item) {
            const { id, disabled_by: disabledBy } = item;
            if (this.selectedId === null) return false;
            return this.selectedId === id && !this.isDelete && !disabledBy;
        },
        handleCancel() {
            this.selectedId = null;
            this.isDelete = false;
        },
        handleCreate() {
            this.selectedId = 0;
        },
        async handleSaveCreate(item) {
            item.content = item.content.trim();
            await this.saveTag(item);
            await this.getAllTags();
        },
        async handleSaveEdit(item) {
            item.content = item.content.trim();
            this.selectedId = null;
            await this.updateTag(item);
            await this.getAllTags();
        },
        handleError(value) {
            // if there is error it throws true
            this.isFormValid = !value;
        },
        setSelectedId(itemId) {
            this.isDelete = true;
            this.selectedId = itemId;
        },
        isUnique(nameVal) {
            const disabledBySystem = 'system';
            const nameValNormalized = nameVal.trim().toLowerCase();

            const isDuplicated =
                nameVal &&
                this.data.some(item => {
                    const isNotDisabled = !item.disabled_by;
                    const isDisabled = item.disabled_by && item.disabled_by === disabledBySystem;
                    const isDifferentId = item.id !== this.selectedId;
                    const hasSameContent = item.content.toLowerCase() === nameValNormalized;

                    return (isNotDisabled || isDisabled) && hasSameContent && isDifferentId;
                });

            return isDuplicated ? 'Tag must be unique' : true;
        },
    },
};
</script>
